import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Smartphone, X, ChevronRight } from "lucide-react";
import {
  dismissBanner,
  getPageContext,
  useBannerDismissed,
  useIsStandalone,
} from "@/hooks/use-pwa-install";

type Props = {
  onOpenDialog: () => void;
};

export function PwaInstallBanner({ onOpenDialog }: Props) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const dismissed = useBannerDismissed();
  const standalone = useIsStandalone();

  if (standalone || dismissed) return null;
  if (location.startsWith("/m")) return null;

  const ctx = getPageContext(location);
  if (!ctx.match) return null;

  return (
    <div
      className="hidden md:block bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800 text-white border-b border-slate-800"
      data-testid="banner-pwa-install"
    >
      <div className="px-4 md:px-6 h-10 flex items-center gap-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
          <Smartphone className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
        </div>

        <p className="text-[12px] text-slate-200 flex-1 min-w-0 truncate">
          <span className="font-semibold text-white">{ctx.title}</span>{" "}
          <span className="text-slate-300">{ctx.message}</span>
        </p>

        <div className="hidden md:flex items-center gap-1.5 text-[10px] text-slate-400 px-2 py-1 rounded-full bg-white/5 border border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          {t("seg04.pwaAvailable")}
        </div>

        <button
          type="button"
          onClick={onOpenDialog}
          className="text-[11px] font-semibold text-white bg-white/10 hover:bg-white/15 rounded-full px-3 py-1 flex items-center gap-1.5 border border-white/10 transition-colors"
          data-testid="button-pwa-install-open"
        >
          {t("seg04.getMobileApp")}
          <ChevronRight className="w-3 h-3" />
        </button>

        <button
          type="button"
          onClick={() => dismissBanner()}
          aria-label={t("seg04.dismiss")}
          className="w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400 shrink-0 transition-colors"
          data-testid="button-pwa-install-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
